import omitBy from 'lodash.omitby'
import isNil from 'lodash.isnil'
import { RealEstate, RealEstateDetails } from '../../../../@types/real-estate-api-types'
import { ContentStateInterface, InteriorQuality, Salutation } from '../../../../@types/types'
import { estateFeaturesToApp } from './estateFeaturesToApp'
import { rentDurationsToApp } from './rentDurationsToApp'
import { addressToApp } from './addressToApp'
import { otherCostsToApp } from './otherCostsToApp'
import { priceToApp } from './priceToApp'
import i18n from '../../../../i18next/i18n'
import {
  AreaCoverageOption,
  ConstructibleAfterOption,
  ConstructionPhaseOption,
  LocationClassificationTypeOption,
  SupplyTypeOption,
} from '../../../../@types/dropdown-types'
import { siteRecommendedUseTypesToApp } from './siteRecommendedUseTypesToApp'
import { getDefaultLogger } from '../../../../services/logger/logger'
import {
  resetConstructionYearIfUnknown,
  resetConsumptionContainsWarmWaterIfNecessary,
  resetEnergyCertificateIfNotAvailable,
} from '../../../../components/pages/insertionPage/insertionEditorValidators/Resets'
import { apiSearchDataToApp } from './apiSearchDataToApp'
import { EN, parseDecimal } from '../../../../i18next/decimal'
import { parseInteger } from '../../../../i18next/integer'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const CLB_DATE_FORMAT = ['D.M.YYYY', 'DD.MM.YYYY']

function removeHomeStreetHomeIfPresent(otherNote: string | undefined) {
  const homeStreetHomeTag = i18n.t('wizard.screen.description.homeStreetHome.tag')
  return otherNote?.replace(homeStreetHomeTag, '')?.trim()
}

function containsHomeStreetHomeTag(otherNote: string | undefined) {
  const homeStreetHomeTag = i18n.t('wizard.screen.description.homeStreetHome.tag')

  return otherNote?.includes(homeStreetHomeTag) ?? false
}

function isValidDate(details: RealEstateDetails, field: keyof RealEstateDetails): boolean {
  const date = details[field]
  const isValid = date && dayjs(date as string, CLB_DATE_FORMAT, true).isValid()
  if (!isValid) {
    getDefaultLogger().info(`Received invalid date: '${date}' in field: '${field}' from CLB backend.`)
  }
  return isValid === true
}

export const listingToApp = (listing: RealEstate, defaultStateValues: ContentStateInterface): ContentStateInterface => {
  const mapping: ContentStateInterface = {
    contactId: listing.contact?.contactId,
    externalContactId: listing.contact?.externalId,
    firstName: listing.contact ? listing.contact.firstName : undefined,
    freeFrom:
      listing.details?.freeFrom && isValidDate(listing.details, 'freeFrom')
        ? dayjs(listing.details.freeFrom, CLB_DATE_FORMAT).toDate()
        : undefined,
    intermediateRent:
      listing.details?.freeUntil && isValidDate(listing.details, 'freeUntil')
        ? true
        : defaultStateValues.intermediateRent,
    freeUntil:
      listing.details?.freeUntil && isValidDate(listing.details, 'freeUntil')
        ? dayjs(listing.details.freeUntil, CLB_DATE_FORMAT).toDate()
        : undefined,
    moveInDate:
      listing.details?.moveInDate && isValidDate(listing.details, 'moveInDate')
        ? dayjs(listing.details.moveInDate, CLB_DATE_FORMAT).toDate()
        : undefined,
    startRentalDate:
      listing.details?.startRentalDate && isValidDate(listing.details, 'startRentalDate')
        ? dayjs(listing.details?.startRentalDate, CLB_DATE_FORMAT).toDate()
        : undefined,
    energyCertificateCreationDate: listing.information?.energyCertificate?.creationDate,
    legalConstructionYear: listing.information?.energyCertificate?.legalConstructionYear,
    energyCertificateAvailability: listing.information?.energyCertificateAvailability,
    consumptionContainsWarmWater: listing.information?.energyCertificate?.consumptionContainsWarmWater,
    thermalCharacteristic: parseDecimal(listing.information?.energyCertificate?.thermalCharacteristic, EN),
    buildingEnergyRatingType: listing.information?.energyCertificate?.buildingEnergyRatingType,
    efficiencyClass: listing.information?.energyCertificate?.efficiencyClass,
    constructionEnergySource:
      listing.information?.energyCertificate?.firingType && listing.information.energyCertificate?.firingType.length
        ? listing.information.energyCertificate.firingType
        : undefined,
    totalFloorsNumber: listing.details?.noFloors,
    floor: parseDecimal(listing.details?.floor, EN),
    constructionHeatingType: listing.details?.heatingType,
    distanceToPT: listing.location?.distanceToPT,
    distanceToMRS: listing.location?.distanceToMRS,
    distanceToAirport: listing.location?.distanceToAirport,
    distanceToFM: listing.location?.distanceToFM,
    constructionYear: listing.information?.constructionYear,
    unknownConstructionYear:
      listing.information?.constructionYearUnknown ?? defaultStateValues.unknownConstructionYear ?? false,
    constructionLastModernization: listing.information?.lastRefurbishment,
    constructionCondition: listing.information?.condition,
    adTitle: listing.title,
    adLocation: listing.additionalInformation?.locationNote,
    adDescription: listing.additionalInformation?.descriptionNote,
    adFurnishingNote: listing.additionalInformation?.furnishingNote,
    adOther: removeHomeStreetHomeIfPresent(listing.additionalInformation?.otherNote),
    isHomeStreetHomeEnabled: containsHomeStreetHomeTag(listing.additionalInformation?.otherNote),

    airConditioning: listing.details?.airConditioning,
    flooringType: listing.details?.flooringType,
    floorLoad: listing.details?.floorLoad,
    powerSupply: parseDecimal(listing.details?.powerSupply, EN),
    noParkingSpaces: listing.details?.garageInformation?.noParkingSpaces,
    parkingSpaceRent: parseDecimal(listing.details?.garageInformation?.parkingSpaceRent, EN),
    parkingSpaceType: listing.details?.garageInformation?.parkingSpaceType,
    loadLifting: listing.details?.loadLifting,
    loadLiftingValue: listing.details?.loadLiftingValue,
    goodsLift: listing.details?.goodsLift,
    goodsLiftLoad: listing.details?.goodsLiftLoad,
    crane: listing.details?.crane,
    craneValue: listing.details?.craneValue,
    priceType: {
      value: listing.price?.priceType ?? defaultStateValues.priceType?.value ?? null,
      label: defaultStateValues.priceType?.label ?? null,
    },
    pricePeriodType: {
      value: listing.price?.pricePeriodType ?? defaultStateValues.pricePeriodType?.value ?? null,
      label: defaultStateValues.pricePeriodType?.label ?? null,
    },
    ...(listing.price ? priceToApp(listing.price) : undefined),
    email: listing.contact ? listing.contact.email : undefined,
    salutation: listing.contact?.salutationType ?? defaultStateValues.salutation ?? Salutation.NO_SALUTATION,
    lastName: listing.contact ? listing.contact.lastName : undefined,
    phoneNumber: listing.contact ? listing.contact.phoneNumber : undefined,
    interiorQuality:
      listing.details?.interiorQuality ?? defaultStateValues.interiorQuality ?? InteriorQuality.NO_INFORMATION,
    rentDurations: rentDurationsToApp(listing, defaultStateValues),
    lanCables: listing.details?.lanCables,
    mainFloorSpace: parseDecimal(listing.details?.mainFloorSpace, EN),
    separableSpace: parseDecimal(listing.details?.separableSpace, EN),
    totalFloorSpace: parseDecimal(listing.details?.totalFloorSpace, EN),
    livingSpace: parseDecimal(listing.details?.livingSpace, EN),
    plotArea: parseDecimal(listing.details?.plotArea, EN),
    room: parseDecimal(listing.details?.noRooms, EN),
    buildingType: {
      value: listing.buildingType ?? defaultStateValues.buildingType?.value ?? null,
      label: listing.buildingType ?? defaultStateValues.buildingType?.label ?? null,
    },
    estateFeatures: estateFeaturesToApp(listing, defaultStateValues),
    siteRecommendedUseTypes: siteRecommendedUseTypesToApp(listing, defaultStateValues),
    ...addressToApp(listing.address),
    usableArea: parseDecimal(listing.details?.usableArea, EN),
    widthGarage: parseDecimal(listing.details?.widthGarage, EN),
    heightGarage: parseDecimal(listing.details?.heightGarage, EN),
    lengthGarage: parseDecimal(listing.details?.lengthGarage, EN),
    hasCourtage: listing.courtageInformation?.hasCourtage,
    courtageNote: listing.courtageInformation?.courtageNote,
    courtage: listing.courtageInformation?.courtage,
    baseRent: parseDecimal(listing.price?.baseRent, EN),
    petsAllowed: listing.details?.petsAllowed,
    ...otherCostsToApp(listing.otherCosts, defaultStateValues),
    noBathrooms: parseDecimal(listing.details?.noBathrooms, EN),
    noBedrooms: parseDecimal(listing.details?.noBedrooms, EN),
    rental: listing.tenantDocuments?.rental,
    selfReport: listing.tenantDocuments?.selfReport,
    solvency: listing.tenantDocuments?.solvency,
    income: listing.tenantDocuments?.income,
    identification: listing.tenantDocuments?.identification,
    minimumTermOfLease: parseDecimal(listing.details?.minimumTermOfLease, EN),
    requestedGender: listing.details?.requestedGender,
    flatShareSize: parseDecimal(listing.details?.flatShareSize, EN),
    smokingAllowed: listing.details?.smokingAllowed,
    numberOfRequestedFlatMates: parseDecimal(listing.details?.noRequestedFlatMates, EN),
    furnishing: listing.details?.furnishing,
    ageOfRequestedFrom: parseDecimal(listing.details?.ageOfRequestedFrom, EN),
    ageOfRequestedTo: parseDecimal(listing.details?.ageOfRequestedTo, EN),
    numberOfMaleFlatMates: parseDecimal(listing.details?.noMaleFlatMates, EN),
    numberOfFemaleFlatMates: parseDecimal(listing.details?.noFemaleFlatMates, EN),
    ageOfFlatMatesFrom: parseDecimal(listing.details?.ageOfFlatMatesFrom, EN),
    ageOfFlatMatesTo: parseDecimal(listing.details?.ageOfFlatMatesTo, EN),
    roomSize: parseDecimal(listing.details?.roomSize, EN),
    rentSubsidy: parseDecimal(listing.details?.rentSubsidy, EN),
    grz: parseDecimal(listing.details?.grz, EN),
    gfz: parseDecimal(listing.details?.gfz, EN),
    minRentalTime: parseDecimal(listing.details?.minRentalTime, EN),
    maxRentalTime: parseDecimal(listing.details?.maxRentalTime, EN),
    maxNumberOfPersons: parseDecimal(listing.details?.maxNumberOfPersons, EN),
    frontWindowLength: parseDecimal(listing.details?.frontWindowLength, EN),
    numberOfSeats: parseDecimal(listing.details?.numberOfSeats, EN),
    numberOfBeds: parseDecimal(listing.details?.numberOfBeds, EN),
    objectName: listing.objectName,
    published: listing.published,
    areaCoverage:
      listing.details?.areaCoverage && listing.details?.areaCoverage === AreaCoverageOption.NO_INFORMATION
        ? undefined
        : listing.details?.areaCoverage,
    constructibleAfter:
      listing.details?.constructibleAfter &&
      listing.details?.constructibleAfter === ConstructibleAfterOption.NO_INFORMATION
        ? undefined
        : listing.details?.constructibleAfter,
    locationClassificationType:
      listing.details?.locationClassificationType &&
      listing.details?.locationClassificationType === LocationClassificationTypeOption.NO_INFORMATION
        ? undefined
        : listing.details?.locationClassificationType,
    supplyType:
      listing.details?.supplyType && listing.details?.supplyType === SupplyTypeOption.NO_INFORMATION
        ? undefined
        : listing.details?.supplyType,
    usageType: listing.details?.utilizationTradeSite,
    constructionPhase:
      listing.details?.constructionPhase &&
      listing.details?.constructionPhase === ConstructionPhaseOption.NO_INFORMATION
        ? undefined
        : listing.details?.constructionPhase,
    rentalIncome: parseDecimal(listing.details?.rentalIncome, EN),
    rented: listing.details?.rented,
    auxiliaryArea: parseDecimal(listing.details?.auxiliaryArea, EN),
    priceMultiplier: parseInteger(listing.details?.priceMultiplier),
    rentalIncomeActual: parseDecimal(listing.details?.rentalIncomeActual, EN),
    rentalIncomeTarget: parseDecimal(listing.details?.rentalIncomeTarget, EN),
    industrialArea: parseDecimal(listing.details?.industrialArea, EN),
    pricePerSQM: parseDecimal(listing.details?.pricePerSQM, EN),
    serviceCharge: parseDecimal(listing.details?.serviceCharge, EN),
    additionalArea: parseDecimal(listing.details?.additionalArea, EN),
    conversionToLeadAllowed: listing.conversionToLeadAllowed ?? false,
    isDepositGuaranteeAllowed: listing.isDepositGuaranteeAllowed ?? undefined,
    externalId: listing.externalId,
    groupNumber: listing.groupNumber,
    ...apiSearchDataToApp(listing),
  }
  const mappedListing = { ...defaultStateValues, ...omitBy(mapping, isNil) }
  resetEnergyCertificateIfNotAvailable(mappedListing)
  resetConstructionYearIfUnknown(mappedListing)
  resetConsumptionContainsWarmWaterIfNecessary(mappedListing, !mappedListing.manualCountry)
  return mappedListing
}
